import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PromoContext = createContext(null);

/**
 * Simple device fingerprint — combines UA + screen + timezone + language.
 * Hashed via FNV-1a to keep it short. Not cryptographic — just stable per device.
 */
const computeFingerprint = () => {
  try {
    const parts = [
      navigator.userAgent || '',
      `${window.screen?.width || 0}x${window.screen?.height || 0}`,
      `${window.screen?.colorDepth || 0}`,
      Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      navigator.language || '',
      navigator.hardwareConcurrency || '',
      navigator.platform || '',
    ].join('|');

    // FNV-1a 32-bit
    let hash = 0x811c9dc5;
    for (let i = 0; i < parts.length; i++) {
      hash ^= parts.charCodeAt(i);
      hash = (hash * 0x01000193) >>> 0;
    }
    return hash.toString(16).padStart(8, '0');
  } catch {
    return 'unknown';
  }
};

/**
 * Returns the deadline (unix ts) and remaining seconds for the discount timer.
 *
 * Priority:
 *  1) Signed bot link  /?uid=&deadline=&sig=  → server verifies HMAC, returns deadline.
 *  2) Visitor session  → server creates/returns a 24h session bound to cookie + IP + fingerprint.
 *
 * If the page is NOT a discount page (discountMode=false) and there's no signed link,
 * the provider stays idle (deadline=null, hasValidPromo=false).
 */
export const PromoProvider = ({ children }) => {
  const [deadline, setDeadline] = useState(null);          // unix ts in seconds
  const [serverNowOffset, setServerNowOffset] = useState(0); // server - client (seconds)
  const [hasValidPromo, setHasValidPromo] = useState(false); // true if signed link verified OR session active
  const [isExpired, setIsExpired] = useState(false);
  const [promoParams, setPromoParams] = useState(null);    // {uid, deadline, sig} — to forward to checkout
  const [loaded, setLoaded] = useState(false);
  const [tick, setTick] = useState(0);

  // Run once on mount — detect page mode + load deadline
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      const path = window.location.pathname;
      const isDiscountPage = path === '/discount' || path === '/faster_discount' || path.startsWith('/discount/');
      const search = new URLSearchParams(window.location.search);
      const uid = search.get('uid');
      const dl = search.get('deadline');
      const sig = search.get('sig');

      // 1) Signed bot link — has top priority. Verify with server.
      if (uid && dl && sig) {
        try {
          const res = await fetch(`${API_URL}/api/promo/verify?uid=${encodeURIComponent(uid)}&deadline=${encodeURIComponent(dl)}&sig=${encodeURIComponent(sig)}`);
          if (!res.ok) throw new Error(`verify ${res.status}`);
          const data = await res.json();
          if (cancelled) return;
          if (data.valid && data.deadline > 0) {
            setDeadline(data.deadline);
            setServerNowOffset(data.now - Math.floor(Date.now() / 1000));
            setHasValidPromo(true);
            setPromoParams({ uid, deadline: parseInt(dl, 10), sig });
            setLoaded(true);
            return;
          }
          // Invalid sig → fall through to no-promo state
          if (cancelled) return;
          setHasValidPromo(false);
          setLoaded(true);
          return;
        } catch (e) {
          if (cancelled) return;
          console.warn('promo verify failed', e);
          // Fall through to session if discount page
        }
      }

      // 2) Visitor session — only on discount pages
      if (isDiscountPage) {
        try {
          const fingerprint = computeFingerprint();
          const res = await fetch(`${API_URL}/api/promo/session`, {
            method: 'POST',
            headers: { 'X-Visitor-Fingerprint': fingerprint },
            // NOTE: do NOT send credentials — cross-origin (vadjik.com → backend) with
            // 'Allow-Origin: *' + credentials is blocked by browsers. Server identifies
            // returning visitors via IP + fingerprint instead of cookie.
          });
          if (!res.ok) throw new Error(`session ${res.status}`);
          const data = await res.json();
          if (cancelled) return;
          setDeadline(data.deadline);
          setServerNowOffset(data.now - Math.floor(Date.now() / 1000));
          setHasValidPromo(true);
          setLoaded(true);
          return;
        } catch (e) {
          if (cancelled) return;
          console.error('promo session failed', e);
        }
      }

      if (cancelled) return;
      setLoaded(true);
    };
    init();
    return () => { cancelled = true; };
  }, []);

  // Heartbeat — re-render every second to refresh remaining time, and detect expiry
  useEffect(() => {
    if (!deadline) return undefined;
    const id = setInterval(() => {
      setTick((t) => t + 1);
      const nowServer = Math.floor(Date.now() / 1000) + serverNowOffset;
      if (nowServer >= deadline) {
        setIsExpired(true);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [deadline, serverNowOffset]);

  const remainingSeconds = useMemo(() => {
    if (!deadline) return 0;
    const nowServer = Math.floor(Date.now() / 1000) + serverNowOffset;
    return Math.max(0, deadline - nowServer);
  }, [deadline, serverNowOffset, tick]);

  // Should discounts be applied? Yes when promo is valid AND not expired.
  const shouldApplyDiscount = hasValidPromo && !isExpired && remainingSeconds > 0;

  const getCheckoutPromoPayload = useCallback(() => {
    if (!promoParams) return {};
    return {
      promo_uid: promoParams.uid,
      promo_deadline: promoParams.deadline,
      promo_sig: promoParams.sig,
    };
  }, [promoParams]);

  const value = useMemo(() => ({
    loaded,
    deadline,
    remainingSeconds,
    isExpired,
    hasValidPromo,
    shouldApplyDiscount,
    promoParams,
    getCheckoutPromoPayload,
  }), [loaded, deadline, remainingSeconds, isExpired, hasValidPromo, shouldApplyDiscount, promoParams, getCheckoutPromoPayload]);

  return <PromoContext.Provider value={value}>{children}</PromoContext.Provider>;
};

export const usePromo = () => {
  const ctx = useContext(PromoContext);
  if (!ctx) {
    // Safe fallback when used outside provider (e.g. tests)
    return {
      loaded: true,
      deadline: null,
      remainingSeconds: 0,
      isExpired: false,
      hasValidPromo: false,
      shouldApplyDiscount: false,
      promoParams: null,
      getCheckoutPromoPayload: () => ({}),
    };
  }
  return ctx;
};
